import React, { useState, useRef, useEffect } from 'react';
import Dropdown from './Dropdown';
import Modal from './Modal';
import { useAuth } from '../../context/AuthContext';
import { updateProfile, uploadAvatar } from '../../services/authService';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import toast from 'react-hot-toast';

// Достижимые вторичные баллы ЕГЭ информатики (из шкалы перевода, без нуля).
// Не каждый балл получить можно — только эти.
// Достижимые тестовые баллы ЕГЭ. Ниже 40 — непроходной порог, целью быть не может.
const EGE_SECONDARY = [40, 43, 46, 48, 51, 54, 56, 59, 62, 64, 67, 70, 72, 75, 78, 80, 83, 85, 88, 90, 93, 95, 98, 100];

// Общая модалка «Редактировать профиль» — для админа и ученика.
// Меняет имя и аватар; после сохранения сразу обновляет user в AuthContext.
const FIELD = {
  width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: 11,
  border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-1)',
  fontSize: 14, fontFamily: 'inherit', outline: 'none',
};

const EditProfileModal = ({ isOpen, onClose, showGoal = false, goal = '', onGoalChange, examType }) => {
  const { user, updateUser } = useAuth();
  const fileRef = useRef(null);

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [goalVal, setGoalVal] = useState('');
  const [file, setFile] = useState(null);       // выбранный новый файл (режим кропа)
  const [preview, setPreview] = useState(null); // существующий аватар / превью
  const [removed, setRemoved] = useState(false);// нажали «Удалить фото»
  const [saving, setSaving] = useState(false);

  // Кроп: масштаб, смещение, натуральный размер картинки
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [nat, setNat] = useState(null);         // { w, h }
  const imgRef = useRef(null);
  const drag = useRef(null);

  const BOX = 200; // размер круга кропа на экране

  // При каждом открытии подставляем актуальные данные пользователя
  useEffect(() => {
    if (isOpen) {
      // В базе имя одной строкой: первое слово — имя, остальное — фамилия
      const parts = (user?.name || '').trim().split(/\s+/).filter(Boolean);
      setFirst(parts[0] || '');
      setLast(parts.slice(1).join(' '));
      setGoalVal(goal || '');
      setFile(null);
      setRemoved(false);
      setPreview(user?.avatar || null);
      setZoom(1); setPos({ x: 0, y: 0 }); setNat(null);
    }
  }, [isOpen, user]); // eslint-disable-line

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Можно загрузить только картинку'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('Файл больше 5 МБ'); return; }
    setFile(f);
    setRemoved(false);
    setZoom(1); setPos({ x: 0, y: 0 }); setNat(null);
    setPreview(URL.createObjectURL(f));
  };

  const removePhoto = () => {
    setFile(null); setRemoved(true); setPreview(null);
    setZoom(1); setPos({ x: 0, y: 0 }); setNat(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const cancelCrop = () => {
    setFile(null); setPreview(user?.avatar || null);
    setZoom(1); setPos({ x: 0, y: 0 }); setNat(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Геометрия картинки в круге: cover при zoom=1, дальше zoom увеличивает.
  const cover = nat ? BOX / Math.min(nat.w, nat.h) : 1;
  const imgW = nat ? nat.w * cover * zoom : 0;
  const imgH = nat ? nat.h * cover * zoom : 0;
  const clampPos = (p) => {
    const mx = Math.max(0, (imgW - BOX) / 2), my = Math.max(0, (imgH - BOX) / 2);
    return { x: Math.max(-mx, Math.min(mx, p.x)), y: Math.max(-my, Math.min(my, p.y)) };
  };
  useEffect(() => { setPos(p => clampPos(p)); /* eslint-disable-next-line */ }, [zoom, nat]);

  const onImgLoad = (e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight });

  const startDrag = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y };
    const move = (ev) => { if (!drag.current) return; setPos(clampPos({ x: drag.current.px + (ev.clientX - drag.current.sx), y: drag.current.py + (ev.clientY - drag.current.sy) })); };
    const up = () => { drag.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  // Экспорт обрезанного квадрата 256×256 из текущего положения/масштаба
  const exportCrop = () => new Promise((resolve) => {
    const img = imgRef.current;
    if (!img || !nat) return resolve(null);
    const O = 256, k = O / BOX;
    const c = document.createElement('canvas'); c.width = O; c.height = O;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, ((BOX - imgW) / 2 + pos.x) * k, ((BOX - imgH) / 2 + pos.y) * k, imgW * k, imgH * k);
    c.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
  });

  const handleSave = async () => {
    // Обратно склеиваем в одну строку — так же, как в приглашении ученика
    const trimmed = [first.trim(), last.trim()].filter(Boolean).join(' ');
    if (!first.trim()) {
      toast.error('Введите имя');
      return;
    }
    setSaving(true);
    try {
      // 1) Если выбрали новое фото — обрезаем по кругу и загружаем на сервер
      if (file) {
        const blob = await exportCrop();
        const cropped = blob ? new File([blob], 'avatar.jpg', { type: 'image/jpeg' }) : file;
        await uploadAvatar(cropped);
      }
      // 2) Обновляем имя; если нажали «Удалить фото» (и не выбрали новое) —
      //    просим сервер обнулить аватар (avatar: null)
      const body = { name: trimmed };
      if (!file && removed) body.avatar = null;
      // Цель по баллам уходит на сервер — иначе её не видно админу в карточке ученика
      if (showGoal) body.goalScore = goalVal || null;
      const res = await updateProfile(body);
      updateUser({ name: res.user.name, avatar: res.user.avatar, goalScore: res.user.goalScore });
      if (showGoal && onGoalChange) onGoalChange(goalVal);

      toast.success('Профиль обновлён');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Редактировать профиль" size="small">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        {/* Круг (кроп при выборе фото, иначе аватар) + заметная кнопка-камера справа-снизу */}
        <div style={{ position: 'relative' }}>
          {file ? (
            <div onPointerDown={startDrag}
              style={{ position: 'relative', width: BOX, height: BOX, borderRadius: '50%', overflow: 'hidden', background: 'var(--bg)', border: '2px solid var(--border)', cursor: 'grab', touchAction: 'none' }}>
              <img ref={imgRef} src={preview} alt="" draggable={false} onLoad={onImgLoad}
                style={{ position: 'absolute', width: imgW || BOX, height: imgH || BOX, left: (BOX - imgW) / 2 + pos.x, top: (BOX - imgH) / 2 + pos.y, userSelect: 'none', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.6)', pointerEvents: 'none' }} />
            </div>
          ) : (
            <div style={{ width: 96, height: 96, borderRadius: '50%', overflow: 'hidden', background: preview ? 'var(--primary)' : avatarColor(user?.id), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--border)' }}>
              {preview
                ? <img src={preview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <AvatarIcon id={user?.id} size={40} />}
            </div>
          )}
          <button type="button" onClick={() => fileRef.current?.click()} aria-label="Заменить фото"
            style={{ position: 'absolute', right: file ? 8 : -2, bottom: file ? 8 : -2, width: 36, height: 36, borderRadius: '50%', border: '3px solid var(--lg-modal, #fff)', background: 'var(--primary)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 10px rgba(40,20,80,0.35)' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
          </button>
        </div>

        {file && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 220 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={e => setZoom(+e.target.value)} style={{ flex: 1, accentColor: 'var(--primary)', cursor: 'pointer' }} />
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="8" y1="11" x2="14" y2="11"/><line x1="11" y1="8" x2="11" y2="14"/></svg>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Перетащите фото и подберите масштаб</div>
          </>
        )}

        <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display: 'none' }} />

        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {file && (
            <button type="button" onClick={cancelCrop}
              style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
              Отмена
            </button>
          )}
          {preview && !file && (
            <button type="button" onClick={removePhoto}
              style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
              Удалить фото
            </button>
          )}
        </div>

        {/* Имя и фамилия — двумя полями в одну строку; если места совсем мало, переносятся */}
        <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 6 }}>
              Имя
            </label>
            <input
              type="text"
              value={first}
              onChange={e => setFirst(e.target.value)}
              placeholder="Иван"
              maxLength={50}
              style={FIELD}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 6 }}>
              Фамилия
            </label>
            <input
              type="text"
              value={last}
              onChange={e => setLast(e.target.value)}
              placeholder="Иванов"
              maxLength={50}
              style={FIELD}
            />
          </div>
        </div>

        {showGoal && (
          <div style={{ width: '100%' }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 6 }}>{examType === 'ОГЭ' ? 'Цель — оценка' : 'Цель по баллам'}</label>
            <Dropdown
              value={goalVal}
              onChange={setGoalVal}
              placeholder="Не выбрана"
              options={[
                { value: '', label: 'Не выбрана' },
                ...(examType === 'ОГЭ'
                  ? [3, 4, 5].map(g => ({ value: String(g), label: `Оценка ${g}` }))
                  : EGE_SECONDARY.map(x => ({ value: String(x), label: `${x} баллов` }))),
              ]}
            />
            <p style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 6 }}>Доступны только достижимые {examType === 'ОГЭ' ? 'оценки' : 'баллы из шкалы перевода'}.</p>
          </div>
        )}

        {/* Кнопки */}
        <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1, padding: '11px', borderRadius: 11, border: '1px solid var(--border)',
              background: 'transparent', color: 'var(--text-2)', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit'
            }}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1, padding: '11px', borderRadius: 11, border: 'none',
              background: 'var(--primary)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit', opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditProfileModal;
